<script setup>
/**
 * One button, one dialog, THREE CHOICES — not three forms stacked down a page.
 *
 * This is the click path THE PLAN §3 costs out: Waslo AI → Answers → Add knowledge → pick
 * one → Save. Five clicks. The screen it replaces put a paste-text form, a crawl form and
 * an upload form permanently on top of the library, so an owner who wanted the third one
 * scrolled past two she did not want, every single time, and the page opened looking like
 * a tax return. A choice is smaller than a form, and only one form exists at a time.
 *
 * THE THREE CARDS STAY VISIBLE AFTER THE PICK. Hiding them would make this a wizard, and
 * a wizard has a back button and a step you can be lost in; here, changing your mind is
 * clicking the other card. What changes below the cards is only the one or two fields that
 * choice needs — so the dialog is never taller than the choice you made requires.
 *
 * WHAT IT REFUSES TO GUESS. Every rule the BFF enforces is checked here first, in the
 * owner's words, so the common mistakes never cost a round trip; and when the BFF answers
 * `validation_failed` anyway, its `fields` are pinned under the exact controls that caused
 * them, because that contract guarantees the keys are the request-body keys
 * (`http-errors.ts`). Anything else it can answer — a rejection with no field to blame, a
 * dead connection, no credits — becomes ONE sentence above the buttons, never a red
 * message under a box the owner did not get wrong.
 *
 * IT NEVER CLAIMS TO HAVE SAVED ANYTHING. A write here returns a JOB, so the dialog closes
 * saying "we're reading it now", and the screen behind it starts watching. Saying "added"
 * would be a lie for the next two minutes, and the source list would be empty when the
 * owner looked for it.
 */
import { computed, nextTick, ref } from 'vue';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';

import WasloFileField from './WasloFileField.vue';
import WasloInput from './WasloInput.vue';
import WasloTextArea from './WasloTextArea.vue';
import { useWasloI18n } from '../i18n.js';
import AiConfirmDialog from './AiConfirmDialog.vue';
import { useAiConfirm } from '../composables/useAiConfirm.js';
import { wasloApi, withConfirm } from '../api/client.js';

const props = defineProps({
  /** Chatwoot account id. Null means this dialog cannot write, and says so by staying shut. */
  accountId: { type: Number, default: null },
});

const emit = defineEmits(['queued']);

const { t } = useWasloI18n();

// P-5.12: a source is read by AI, so Add wears the price and asks first. The charge happens
// when the source is really read; the Answers page prints that receipt on the job.
const addCharge = useAiConfirm({
  accountId: computed(() => props.accountId),
  op: 'add_source',
});
const submitLabel = computed(() =>
  addCharge.label(t('WASLO.ANSWERS.DIALOG.SUBMIT'))
);

/** The wire limits, checked here so the commonest mistakes cost no round trip. */
const MAX_TITLE = 200;
const MAX_CONTENT = 200_000;
/** `config.MAX_PDF_UPLOAD_BYTES` — 10 MB. */
const MAX_PDF_BYTES = 10 * 1024 * 1024;
/** P-6.7: the BFF's `MAX_PHOTOS` and `MAX_PHOTO_BYTES` (`routes/api/documents.ts`). */
const MAX_PHOTOS = 10;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const CHOICES = [
  {
    id: 'text',
    label: 'WASLO.ANSWERS.DIALOG.CHOICES.TEXT.LABEL',
    description: 'WASLO.ANSWERS.DIALOG.CHOICES.TEXT.DESCRIPTION',
  },
  {
    id: 'url',
    label: 'WASLO.ANSWERS.DIALOG.CHOICES.URL.LABEL',
    description: 'WASLO.ANSWERS.DIALOG.CHOICES.URL.DESCRIPTION',
  },
  {
    id: 'pdf',
    label: 'WASLO.ANSWERS.DIALOG.CHOICES.PDF.LABEL',
    description: 'WASLO.ANSWERS.DIALOG.CHOICES.PDF.DESCRIPTION',
  },
  // P-6.7: photos of a menu or a price list, read as one source.
  {
    id: 'photos',
    label: 'WASLO.PHOTOS.CHOICE',
    description: 'WASLO.PHOTOS.CHOICE_HINT',
  },
];

const dialogRef = ref(null);
const choice = ref('text');
const title = ref('');
const text = ref('');
const url = ref('');
const file = ref(null);
const photos = ref([]);
const isSaving = ref(false);
const fieldErrors = ref({});
const formError = ref('');

const choices = computed(() =>
  CHOICES.map(item => ({
    id: item.id,
    label: t(item.label),
    description: t(item.description),
  }))
);

const reset = () => {
  choice.value = 'text';
  title.value = '';
  text.value = '';
  url.value = '';
  file.value = null;
  photos.value = [];
  isSaving.value = false;
  fieldErrors.value = {};
  formError.value = '';
};

/**
 * @param {{ type?: 'text'|'url'|'pdf', title?: string }} [prefill] Used by the "add it
 *   again" button on a failed source: the name is carried over, the content is not,
 *   because the thing that failed is exactly the thing that has to change.
 */
const open = (prefill = {}) => {
  reset();
  if (prefill.type) choice.value = prefill.type;
  if (prefill.title) title.value = prefill.title;
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

/** Switching choice drops the errors that belonged to the other one. */
const selectChoice = id => {
  if (choice.value === id) return;
  choice.value = id;
  fieldErrors.value = {};
  formError.value = '';
};

// ── what we check before spending a request ─────────────────────────────────────────
const validate = () => {
  const errors = {};
  const name = title.value.trim();
  if (!name) {
    errors.title = t('WASLO.ANSWERS.DIALOG.NAME_REQUIRED');
  } else if (name.length > MAX_TITLE) {
    errors.title = t('WASLO.ANSWERS.DIALOG.NAME_LONG');
  }

  if (choice.value === 'text') {
    const body = text.value.trim();
    if (!body) errors.content = t('WASLO.ANSWERS.DIALOG.TEXT_REQUIRED');
    else if (body.length > MAX_CONTENT) {
      errors.content = t('WASLO.ANSWERS.DIALOG.TEXT_LONG');
    }
  } else if (choice.value === 'url') {
    const address = url.value.trim();
    let parsed = null;
    try {
      parsed = new URL(address);
    } catch {
      parsed = null;
    }
    if (
      !parsed ||
      (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')
    ) {
      errors.url = t('WASLO.ANSWERS.DIALOG.URL_INVALID');
    }
  } else if (choice.value === 'photos') {
    if (photos.value.length === 0) {
      errors.files = t('WASLO.ANSWERS.DIALOG.FILE_REQUIRED');
    } else if (photos.value.length > MAX_PHOTOS) {
      errors.files = t('WASLO.PHOTOS.TOO_MANY');
    } else if (photos.value.some(photo => !PHOTO_TYPES.includes(photo.type))) {
      errors.files = t('WASLO.PHOTOS.NOT_PHOTO');
    } else if (photos.value.some(photo => photo.size > MAX_PHOTO_BYTES)) {
      errors.files = t('WASLO.PHOTOS.TOO_BIG');
    }
  } else if (!file.value) {
    errors.file = t('WASLO.ANSWERS.DIALOG.FILE_REQUIRED');
  } else if (file.value.size > MAX_PDF_BYTES) {
    errors.file = t('WASLO.ANSWERS.DIALOG.FILE_LARGE');
  }

  fieldErrors.value = errors;
  return Object.keys(errors).length === 0;
};

const submit = async () => {
  if (isSaving.value || props.accountId === null) return;
  formError.value = '';
  if (!validate()) return;

  isSaving.value = true;
  const name = title.value.trim();
  try {
    const outcome = await addCharge.confirmAndRun({
      run: credits => {
        if (choice.value === 'photos') {
          return wasloApi.uploadDocumentImages(
            props.accountId,
            { title: name, files: photos.value },
            undefined,
            withConfirm(credits)
          );
        }
        return choice.value === 'pdf'
          ? wasloApi.uploadDocumentPdf(
              props.accountId,
              { title: name, file: file.value },
              undefined,
              withConfirm(credits)
            )
          : wasloApi.addDocument(
              props.accountId,
              choice.value === 'text'
                ? { type: 'text', title: name, content: text.value.trim() }
                : { type: 'url', title: name, url: url.value.trim() },
              undefined,
              withConfirm(credits)
            );
      },
    });
    // Cancel costs nothing, and the form stays as she filled it.
    if (outcome.cancelled) return;
    const job = outcome.result;
    // The screen behind us owns the "we're reading it now" line and the watch — it is the
    // thing still on the page after this dialog is gone.
    emit('queued', job);
    close();
  } catch (error) {
    // `validation_failed` ALWAYS carries `fields`, keyed by the request-body key, and no
    // other code ever does (`http-errors.ts`) — so this branch needs no guard, and every
    // other failure is a sentence with nothing to pin it to.
    const fields = error?.fields;
    if (fields && typeof fields === 'object') {
      fieldErrors.value = { ...fields };
    } else {
      formError.value = error?.message || t('WASLO.ANSWERS.DIALOG.FAILED');
    }
    // The banner is above the fold of a scrolled dialog; the fields may not be.
    await nextTick();
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    position="top"
    overflow-y-auto
    :title="t('WASLO.ANSWERS.DIALOG.TITLE')"
    :description="t('WASLO.ANSWERS.DIALOG.DESCRIPTION')"
    :confirm-button-label="submitLabel"
    :cancel-button-label="t('WASLO.ANSWERS.DIALOG.CANCEL')"
    :is-loading="isSaving"
    :disable-confirm-button="isSaving || accountId === null"
    data-waslo-add-dialog
    @confirm="submit"
    @close="reset"
  >
    <div class="flex flex-col gap-6">
      <!-- The choice. Three cards, always all three, so changing your mind is one click
           and never a step backwards. -->
      <div class="flex flex-col gap-3" data-waslo-add-choices>
        <RadioCard
          v-for="option in choices"
          :id="option.id"
          :key="option.id"
          :label="option.label"
          :description="option.description"
          :is-active="choice === option.id"
          @select="selectChoice"
        />
      </div>

      <span class="w-full h-px bg-n-weak" />

      <!-- Only the fields the chosen source needs. Every one of them is a Waslo control,
           so what the owner types gets its own direction (R7). -->
      <div class="flex flex-col gap-6">
        <WasloInput
          v-model="title"
          :label="t('WASLO.ANSWERS.DIALOG.NAME_LABEL')"
          :hint="t('WASLO.ANSWERS.DIALOG.NAME_HINT')"
          :placeholder="t('WASLO.ANSWERS.DIALOG.NAME_PLACEHOLDER')"
          :message="fieldErrors.title || ''"
          message-type="error"
        />

        <WasloTextArea
          v-if="choice === 'text'"
          v-model="text"
          :label="t('WASLO.ANSWERS.DIALOG.TEXT_LABEL')"
          :hint="t('WASLO.ANSWERS.DIALOG.TEXT_HINT')"
          :placeholder="t('WASLO.ANSWERS.DIALOG.TEXT_PLACEHOLDER')"
          :message="fieldErrors.content || ''"
          message-type="error"
          min-height="9rem"
          max-height="18rem"
        />

        <WasloInput
          v-else-if="choice === 'url'"
          v-model="url"
          type="url"
          :label="t('WASLO.ANSWERS.DIALOG.URL_LABEL')"
          :hint="t('WASLO.ANSWERS.DIALOG.URL_HINT')"
          :placeholder="t('WASLO.ANSWERS.DIALOG.URL_PLACEHOLDER')"
          :message="fieldErrors.url || ''"
          message-type="error"
        />

        <WasloFileField
          v-else-if="choice === 'photos'"
          v-model="photos"
          multiple
          accept="image/jpeg,image/png,image/webp"
          :label="t('WASLO.PHOTOS.LABEL')"
          :hint="t('WASLO.PHOTOS.HINT')"
          :choose-label="t('WASLO.ANSWERS.DIALOG.FILE_CHOOSE')"
          :change-label="t('WASLO.ANSWERS.DIALOG.FILE_CHANGE')"
          :empty-label="t('WASLO.ANSWERS.DIALOG.FILE_NONE')"
          :message="fieldErrors.files || ''"
          message-type="error"
          data-waslo-add-photos
        />

        <WasloFileField
          v-else
          v-model="file"
          accept="application/pdf,.pdf"
          :label="t('WASLO.ANSWERS.DIALOG.FILE_LABEL')"
          :hint="t('WASLO.ANSWERS.DIALOG.FILE_HINT')"
          :choose-label="t('WASLO.ANSWERS.DIALOG.FILE_CHOOSE')"
          :change-label="t('WASLO.ANSWERS.DIALOG.FILE_CHANGE')"
          :empty-label="t('WASLO.ANSWERS.DIALOG.FILE_NONE')"
          :message="fieldErrors.file || ''"
          message-type="error"
        />
      </div>

      <!-- Where a refusal with nothing to blame goes: above the buttons, in one sentence,
           never under a control the owner filled in correctly. -->
      <Banner v-if="formError" color="ruby" data-waslo-add-error>
        {{ formError }}
      </Banner>
    </div>
  </Dialog>
  <AiConfirmDialog :controller="addCharge" />
</template>
